/**
 * Suspicious v2 §4.3 — Settings → Install → Blocking card.
 *
 * One place a merchant can:
 *   - toggle per-site Blocking on/off (ConfirmDialog on both edges)
 *   - see the Worker version + whether the install can enforce
 *   - review every live block (Unblock, Change duration)
 *   - review every trusted address (Remove)
 *
 * Owner / admin only; reviewer / viewer get a read-only view (no
 * action buttons). All writes route through the typed clients in
 * `apps/dashboard/src/api/threats.ts`.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';

import type {
  BlockingResponse,
  BlocklistEntryWire,
  ManualBlockDuration,
  ProtectedPathsResponse,
} from '@sill/types/api';

import { useOptionalHttp } from '../../api/http-context.js';
import { useOptionalAuth } from '../../app/auth-context.js';
import {
  deleteBlock,
  getBlocking,
  getProtectedPaths,
  listBlocks,
  putFirewallMode,
  putProtectedPathsCustom,
  putProtectedPathsExceptions,
  putProtectedPathsMode,
} from '../../api/threats.js';
import type { ApiError } from '../../api/http-client.js';
import { Card, ConfirmDialog } from '../../components/atoms/index.js';
import { BlockedAddressTable } from './AddressTables.js';
import { SecurityActionsTable } from './SecurityActionsTable.js';
import { useC } from '../../lib/theme.js';
import { BlockStatusLine } from '../mandates/BlockControl.js';
import type { Site } from '@sill/types';
import { blockingShopifyCopy, serverInstallOf } from '../security/server-install.js';
import { SHOPIFY_SAFE_WORKER_AVAILABLE } from '../onboarding/install-copy.js';

const monoFont = "'JetBrains Mono', monospace";
const sansFont = "'Geist', sans-serif";

export interface SettingsInstallBlockingProps {
  readonly site: Site;
  /** Founder 2026-10-20: callback for the "→ Trusted" one-line
   *  pointer. Rendered only when supplied (i.e. inside the Security
   *  shell); omitted in older callers so Settings doesn't move. */
  readonly onOpenTrusted?: () => void;
  /** Founder bug 2026-10-20 (Report 6): clicking a blocked row opens
   *  that sender in Security › Attackers. When present, {@link
   *  BlockedAddressTable} wires each row as a button. Supplied by the
   *  Security tab shell; omitted from Settings so clicks there don't
   *  jump between tabs. */
  readonly onOpenSender?: (block: BlocklistEntryWire) => void;
}

export function SettingsInstallBlocking({ site, onOpenTrusted, onOpenSender }: SettingsInstallBlockingProps): JSX.Element {
  const C = useC();
  const http = useOptionalHttp();
  const auth = useOptionalAuth();
  const role = auth !== null && auth.state.kind === 'authenticated'
    ? auth.state.user.role : null;
  const canMutate = role === 'owner' || role === 'admin';
  const currentUserId = auth !== null && auth.state.kind === 'authenticated'
    ? auth.state.user.user_id : null;

  const [blocking, setBlocking] = useState<BlockingResponse | null>(null);
  const [blocks, setBlocks] = useState<readonly BlocklistEntryWire[]>([]);
  // Founder 2026-10-20: track first-fetch so the BlockedAddressTable
  // never flashes "No blocked addresses" before the server responds.
  const [loaded, setLoaded] = useState(false);
  const [confirmOn, setConfirmOn] = useState(false);
  const [confirmOff, setConfirmOff] = useState(false);
  const [pending, setPending] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const refetch = useCallback(async () => {
    if (http === null) return;
    try {
      const [b, bl] = await Promise.all([
        getBlocking(http, site.site_id),
        listBlocks(http, site.site_id),
      ]);
      setBlocking(b);
      setBlocks(bl.entries);
    } catch { /* keep prior state on a flap */ } finally {
      setLoaded(true);
    }
  }, [http, site.site_id]);

  useEffect(() => { void refetch(); }, [refetch]);
  // Founder 2026-10-18: auto-blocks made elsewhere (campaign panel,
  // background sweep) didn't appear until a reload. Refresh when the tab
  // regains focus.
  useEffect(() => {
    const onFocus = (): void => { if (document.visibilityState === 'visible') void refetch(); };
    document.addEventListener('visibilitychange', onFocus);
    window.addEventListener('focus', onFocus);
    return () => {
      document.removeEventListener('visibilitychange', onFocus);
      window.removeEventListener('focus', onFocus);
    };
  }, [refetch]);

  const flipMode = useCallback(async (next: 'enforce' | 'observe') => {
    if (http === null) return;
    setPending('mode');
    setError(null);
    try {
      const res = await putFirewallMode(http, site.site_id, next);
      if (res.ok) {
        setConfirmOn(false);
        setConfirmOff(false);
        await refetch();
      } else if (res.error === 'install_cannot_enforce') {
        setError('This install can’t enforce yet. Update the Cloudflare Worker, then try again.');
      } else if (res.error === 'forbidden') {
        setError('Only owners and admins can change blocking.');
      } else {
        setError('That didn’t work. Try again.');
      }
    } finally {
      setPending(null);
    }
  }, [http, site.site_id, refetch]);

  const onUnblock = useCallback(async (block_id: string) => {
    if (http === null) return;
    setPending(`unblock:${block_id}`);
    setError(null);
    try {
      await deleteBlock(http, site.site_id, block_id);
      await refetch();
    } catch (e) {
      const msg = (e as ApiError | undefined)?.message ?? 'Could not unblock. Try again.';
      setError(msg);
    } finally {
      setPending(null);
    }
  }, [http, site.site_id, refetch]);

  const blocking_state = blocking?.blocking ?? { mode: 'off' as const, install: 'worker_ready' as const, delivery: null };
  const may_turn_on = canMutate && blocking_state.mode === 'off' && blocking_state.install === 'worker_ready';
  const may_turn_off = canMutate && blocking_state.mode === 'on';

  return (
    <Card data-testid="settings-install-blocking">
      <div style={{ padding: '20px 24px 24px' }}>
        <h3 style={{ fontFamily: sansFont, fontSize: 15, fontWeight: 600, color: C.text, margin: '0 0 4px' }}>
          Blocking
        </h3>
        {/* Founder 2026-10-20: say what this switch does, and how it
            differs from Attack paths. */}
        <div data-testid="blocking-explainer" style={{ fontFamily: sansFont, fontSize: 13, color: C.body, lineHeight: 1.55, marginBottom: 8 }}>
          Stops the addresses on your block list from opening <strong>any page</strong> of {site.domain ?? 'this site'}. They get “Access denied” from the Sill Cloudflare Worker{blocking?.blocking?.install_source === 'plugin' ? ', which this site does not have: the WordPress plugin cannot block addresses' : ''}. Addresses go on the list when you click Block on a sender, or through campaign auto-block. Verified search and AI crawlers are never blocked.
          {/* Founder 2026-10-27: link straight to the Cloudflare card when
              the site has no Worker that can block yet. */}
          {blocking_state.install === 'not_in_path'
            && serverInstallOf(site, { install_source: blocking?.blocking?.install_source ?? null }) === 'shopify_none'
            && !SHOPIFY_SAFE_WORKER_AVAILABLE ? (
            // Founder 2026-10-29: the Worker is not offered for Shopify
            // until its Shopify mode is released; never link to it here.
            <span data-testid="blocking-shopify-note">{' '}{blockingShopifyCopy()}</span>
          ) : blocking_state.install !== 'worker_ready' ? (
            <>
              {' '}
              <a
                href="?view=settings&settings_tab=install&settings_section=cloudflare"
                data-testid="blocking-cloudflare-link"
                style={{ color: C.text, textDecoration: 'underline' }}
              >
                {blocking_state.install === 'worker_outdated'
                  ? 'Update the Cloudflare Worker →'
                  : 'Set up the Cloudflare Worker →'}
              </a>
            </>
          ) : null}
        </div>
        <div style={{ fontFamily: sansFont, fontSize: 12, color: C.muted, lineHeight: 1.5, marginBottom: 12 }}>
          To refuse requests for secret files from everyone, without blocking whole addresses, use Attack paths.
        </div>
        <div style={{ fontFamily: sansFont, fontSize: 13, fontWeight: 600, color: C.text, marginBottom: 12 }}>
          {blocking_state.mode === 'on'
            ? `Blocking is on for ${site.domain ?? 'this site'}.`
            : `Blocking is off for ${site.domain ?? 'this site'}.`}
        </div>

        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
          {may_turn_on ? (
            <button
              type="button"
              data-testid="blocking-turn-on"
              onClick={() => setConfirmOn(true)}
              disabled={pending === 'mode'}
              style={{
                background: C.lime, color: C.ink,
                border: `1px solid ${C.lime}`, borderRadius: 2,
                padding: '7px 12px', fontFamily: sansFont, fontSize: 13,
                cursor: pending === 'mode' ? 'wait' : 'pointer',
              }}
            >
              Turn on blocking
            </button>
          ) : null}
          {may_turn_off ? (
            <button
              type="button"
              data-testid="blocking-turn-off"
              onClick={() => setConfirmOff(true)}
              disabled={pending === 'mode'}
              style={{
                background: 'transparent', color: C.text,
                border: `1px solid ${C.border}`, borderRadius: 2,
                padding: '7px 12px', fontFamily: sansFont, fontSize: 13,
                cursor: pending === 'mode' ? 'wait' : 'pointer',
              }}
            >
              Turn off blocking
            </button>
          ) : null}
        </div>

        <BlockStatusLine
          blocking={blocking_state}
          siteDomain={site.domain ?? null}
        />

        {error !== null ? (
          <div data-testid="blocking-error" style={{
            marginTop: 10, fontFamily: sansFont, fontSize: 12, color: C.coralFg,
          }}>
            {error}
          </div>
        ) : null}

        <div style={{ marginTop: 20, borderTop: `1px solid ${C.borderSoft}`, paddingTop: 14 }}>
          <BlockedAddressTable
            blocks={blocks}
            loading={!loaded}
            canMutate={canMutate}
            pendingId={pending?.startsWith('unblock:') === true ? pending.slice('unblock:'.length) : null}
            onUnblock={(id) => void onUnblock(id)}
            currentUserId={currentUserId}
            {...(onOpenSender !== undefined ? { onOpenSender } : {})}
          />
        </div>

        <div
          data-testid="blocking-trusted-pointer"
          style={{
            marginTop: 20, borderTop: `1px solid ${C.borderSoft}`, paddingTop: 14,
            fontFamily: sansFont, fontSize: 13, color: C.muted,
          }}
        >
          Trusted addresses are never blocked{' '}
          {onOpenTrusted !== undefined ? (
            <button
              type="button"
              data-testid="blocking-trusted-link"
              onClick={onOpenTrusted}
              style={{
                background: 'transparent', border: 'none', padding: 0,
                fontFamily: sansFont, fontSize: 13, color: C.text,
                textDecoration: 'underline', cursor: 'pointer',
              }}
            >
              → Trusted
            </button>
          ) : (
            <span style={{ color: C.text }}>→ Trusted</span>
          )}
          .
        </div>

        {/* Security · Recent changes — the "who changed what, when"
            action trail for this site. Reads existing audit log rows;
            no writes. */}
        <div style={{ marginTop: 20, borderTop: `1px solid ${C.borderSoft}`, paddingTop: 14 }}>
          <SecurityActionsTable site_id={site.site_id} />
        </div>
      </div>

      <ConfirmDialog
        open={confirmOn}
        onClose={() => setConfirmOn(false)}
        title={`Turn on blocking for ${site.domain ?? 'this site'}?`}
        body={`Requests from the ${blocks.length === 1 ? '1 address' : `${blocks.length} addresses`} you've blocked will get “Access denied” within a minute. Verified search and AI crawlers are never blocked. You can turn this off any time.`}
        confirmLabel="Turn on blocking"
        confirmTone="lime"
        onConfirm={() => void flipMode('enforce')}
      />
      <ConfirmDialog
        open={confirmOff}
        onClose={() => setConfirmOff(false)}
        title="Turn off blocking?"
        body="Blocked addresses will reach your site again. Your block list is kept."
        confirmLabel="Turn off"
        confirmTone="coral"
        onConfirm={() => void flipMode('observe')}
      />
    </Card>
  );
}

// Mark unused imports as used (duration prop is unused today — kept for A2
// polish when PATCH duration lands behind a Change-duration button).
export type _BlockingDurationExport = ManualBlockDuration;

/**
 * ADR-0248 S1 (observe-only) + S3 — Settings → Blocking → Protected
 * paths card.
 *
 * S1 shipped the counter + category list; S3 adds:
 *   - Mode switch (Observe / Enforce) with a confirm dialog naming the
 *     site domain and showing the last-7-day would-refuse count.
 *   - "Refused N requests" counter when enforcing.
 *   - Custom paths editor (Tier B) — glob-lite, max 50.
 *   - Exceptions editor (Tier C) — glob-lite, max 20.
 *
 * The built-in Tier A list stays collapsible.
 *
 * Role gate: owner / admin may mutate; reviewer / viewer read-only.
 * `state.may_mutate` from the server is the source of truth; `role`
 * is a UI-level sanity check only.
 */
export interface ProtectedPathsCardProps {
  readonly site: Site;
}

export function ProtectedPathsCard({ site }: ProtectedPathsCardProps): JSX.Element | null {
  const C = useC();
  const http = useOptionalHttp();
  const auth = useOptionalAuth();
  const role = auth !== null && auth.state.kind === 'authenticated'
    ? auth.state.user.role : null;
  const [state, setState] = useState<ProtectedPathsResponse | null>(null);
  const [showCategories, setShowCategories] = useState(false);
  const [confirmFlip, setConfirmFlip] = useState<'observe' | 'enforce' | null>(null);
  const [pending, setPending] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const refetch = useCallback(async () => {
    if (http === null) return;
    try {
      const res = await getProtectedPaths(http, site.site_id);
      setState(res);
    } catch { /* keep null; card hides on fetch failure */ }
  }, [http, site.site_id]);

  useEffect(() => {
    void refetch();
  }, [refetch]);

  const flipMode = useCallback(async (next: 'observe' | 'enforce') => {
    if (http === null || state === null) return;
    setPending('mode');
    setError(null);
    const r = await putProtectedPathsMode(http, site.site_id, next);
    if (!r.ok) {
      if (r.error === 'install_cannot_enforce') {
        // MAJOR 2 — show the versions so the merchant knows what to update to.
        setError(`Update your Worker to ${r.min_enforcing_worker_version} to refuse requests. Your current version is ${r.install_version ?? 'unknown'}.`);
      } else {
        setError(describeModeError(r.error));
      }
    } else {
      await refetch();
    }
    setPending(null);
    setConfirmFlip(null);
  }, [http, refetch, site.site_id, state]);

  if (state === null) return null;
  const count = state.would_refuse_count_window;
  const refused = state.refused_count_window;
  const window_days = state.window_days;
  const canMutate = state.may_mutate && (role === 'owner' || role === 'admin');

  return (
    <>
      <Card data-testid="settings-install-protected-paths">
        <div style={{ padding: '20px 24px 24px' }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 4 }}>
            <h3 style={{ fontFamily: sansFont, fontSize: 15, fontWeight: 600, color: C.text, margin: 0 }}>
              Attack paths
            </h3>
            <div
              data-testid="protected-paths-mode"
              style={{
                fontFamily: monoFont, fontSize: 9, letterSpacing: 2,
                color: C.muted, textTransform: 'uppercase',
              }}
            >
              {state.mode === 'enforce' ? 'ON · REFUSING' : 'OFF · COUNTING ONLY'}
            </div>
          </div>
          <div data-testid="protected-paths-explainer" style={{ fontFamily: sansFont, fontSize: 13, color: C.body, lineHeight: 1.55, marginBottom: 8 }}>
            Refuses requests for <strong>attack paths only</strong> (environment files, secret keys, backups, webshells, debug pages), from anyone, with a plain “not found”. No address is blocked: the same visitor can still open every normal page.
          </div>
          <div style={{ fontFamily: sansFont, fontSize: 13, color: C.muted, marginBottom: 14 }}>
            {state.mode === 'enforce'
              ? 'On: these requests are refused by Sill’s Worker and never reach your server.'
              : 'Off: these requests still reach your server. Sill only counts them, so you can see what would be refused before you turn it on.'}
          </div>

          {state.mode === 'enforce' ? (
            <div
              data-testid="protected-paths-refused-counter"
              style={{
                padding: '14px 16px',
                border: `1px solid ${C.borderSoft}`,
                borderRadius: 3,
                display: 'flex',
                alignItems: 'baseline',
                gap: 10,
                flexWrap: 'wrap',
              }}
            >
              <div style={{ fontFamily: sansFont, fontSize: 20, fontWeight: 600, color: C.text }}>
                {refused.toLocaleString('en-US')}
              </div>
              <div style={{ fontFamily: sansFont, fontSize: 13, color: C.muted }}>
                {state.delivery === 'live'
                  ? `refused in the last ${window_days} days`
                  : `refused in the last ${window_days} days (your Worker has not picked up the new setting yet)`}
              </div>
            </div>
          ) : (
            <div
              data-testid="protected-paths-counter"
              style={{
                padding: '14px 16px',
                border: `1px solid ${C.borderSoft}`,
                borderRadius: 3,
                display: 'flex',
                alignItems: 'baseline',
                gap: 10,
                flexWrap: 'wrap',
              }}
            >
              <div style={{ fontFamily: sansFont, fontSize: 20, fontWeight: 600, color: C.text }}>
                {count.toLocaleString('en-US')}
              </div>
              <div style={{ fontFamily: sansFont, fontSize: 13, color: C.muted }}>
                would have been refused in the last {window_days} days
              </div>
            </div>
          )}

          {/*
            ADR-0248 delivery-state honesty: when the merchant is in
            enforce but the Worker has not yet picked up the fresh
            envelope, DO NOT claim "Refusing". The server computed
            `delivery` from the published-envelope row + mode match;
            we render the honest line here. One source of truth.
          */}
          {state.mode === 'enforce' && state.delivery !== 'live' ? (
            <div
              data-testid="protected-paths-delivery"
              style={{
                fontFamily: sansFont,
                fontSize: 12,
                color: C.muted,
                marginTop: 10,
                padding: '8px 12px',
                border: `1px solid ${C.amber}`,
                borderRadius: 2,
              }}
            >
              {state.delivery === 'pending'
                ? 'Attack paths are on — waiting for your Worker to pick it up (usually within a minute).'
                : state.delivery === 'install_too_old'
                  ? `Update your Worker to ${state.min_enforcing_worker_version} to refuse requests. Your current version is ${state.install_version ?? 'unknown'}.`
                  : 'Attack paths are on, but your Worker has not picked up the setting. Check that the Cloudflare Worker install is up to date.'}
            </div>
          ) : null}

          {state.mode === 'observe' ? (
            <div style={{ fontFamily: sansFont, fontSize: 12, color: C.muted, marginTop: 10 }}>
              Sill recommends watching the count for a few days. If it only shows attack paths, turn them on. You can turn it off again any time.
            </div>
          ) : null}

          {canMutate ? (
            <div style={{ marginTop: 14, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
              {state.mode === 'observe' ? (
                <>
                  <button
                    type="button"
                    data-testid="protected-paths-flip-enforce"
                    disabled={pending !== null || !state.install_can_enforce}
                    onClick={() => setConfirmFlip('enforce')}
                    style={{
                      // MAJOR 2 — dim the primary when the install is
                      // below the enforce floor. The surrounding
                      // explanation points at the Worker update.
                      background: state.install_can_enforce ? C.lime : 'transparent',
                      color: state.install_can_enforce ? C.ink : C.muted,
                      border: `1px solid ${C.border}`,
                      borderRadius: 2,
                      padding: '8px 14px',
                      fontFamily: sansFont,
                      fontSize: 13,
                      fontWeight: 600,
                      cursor: (pending === null && state.install_can_enforce) ? 'pointer' : 'default',
                    }}
                  >
                    Turn on attack paths
                  </button>
                  {!state.install_can_enforce ? (
                    <span
                      data-testid="protected-paths-install-gate"
                      style={{ fontFamily: sansFont, fontSize: 12, color: C.muted }}
                    >
                      Update your Worker to {state.min_enforcing_worker_version} to refuse requests.
                      Your current version is {state.install_version ?? 'unknown'}.
                    </span>
                  ) : null}
                </>
              ) : (
                <button
                  type="button"
                  data-testid="protected-paths-flip-observe"
                  disabled={pending !== null}
                  onClick={() => setConfirmFlip('observe')}
                  style={{
                    background: 'transparent',
                    color: C.text,
                    border: `1px solid ${C.border}`,
                    borderRadius: 2,
                    padding: '8px 14px',
                    fontFamily: sansFont,
                    fontSize: 13,
                    cursor: pending === null ? 'pointer' : 'default',
                  }}
                >
                  Turn off attack paths
                </button>
              )}
            </div>
          ) : null}

          {error !== null ? (
            <div
              data-testid="protected-paths-error"
              style={{
                marginTop: 10,
                padding: '8px 12px',
                border: `1px solid ${C.coral}`,
                borderRadius: 2,
                fontFamily: sansFont, fontSize: 12, color: C.text,
              }}
            >
              {error}
            </div>
          ) : null}

          {state.top_paths.length > 0 ? (
            <div style={{ marginTop: 16 }}>
              <div style={{
                fontFamily: monoFont, fontSize: 9, letterSpacing: 2,
                color: C.muted, textTransform: 'uppercase',
              }}>
                TOP MATCHED PATHS &middot; {state.top_paths.length}
              </div>
              <ul
                data-testid="protected-paths-top-list"
                style={{ listStyle: 'none', margin: '8px 0 0 0', padding: 0 }}
              >
                {state.top_paths.map((p) => (
                  <li
                    key={p.path}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '1fr auto',
                      gap: 12,
                      alignItems: 'center',
                      padding: '6px 0',
                      borderBottom: `1px solid ${C.borderSoft}`,
                    }}
                  >
                    <div
                      style={{
                        fontFamily: monoFont,
                        fontSize: 12,
                        color: C.text,
                        overflowWrap: 'anywhere',
                      }}
                    >
                      {p.path}
                    </div>
                    <div style={{ fontFamily: monoFont, fontSize: 12, color: C.muted }}>
                      {p.count.toLocaleString('en-US')}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          <ProtectedPathsListEditor
            kind="custom"
            site_id={site.site_id}
            http={http}
            canMutate={canMutate}
            cap={state.caps.custom_paths}
            maxLength={state.caps.pattern_max_length}
            entries={state.custom_paths}
            onSaved={refetch}
          />
          <ProtectedPathsListEditor
            kind="exceptions"
            site_id={site.site_id}
            http={http}
            canMutate={canMutate}
            cap={state.caps.exceptions}
            maxLength={state.caps.pattern_max_length}
            entries={state.exceptions}
            onSaved={refetch}
          />

          <div style={{ marginTop: 16 }}>
            <button
              type="button"
              data-testid="protected-paths-toggle-categories"
              onClick={() => setShowCategories((v) => !v)}
              style={{
                background: 'transparent',
                color: C.text,
                border: `1px solid ${C.border}`,
                borderRadius: 2,
                padding: '6px 10px',
                fontFamily: sansFont,
                fontSize: 12,
                cursor: 'pointer',
              }}
            >
              {showCategories ? 'Hide the built-in list' : "What's on the built-in list?"}
            </button>
            {showCategories ? (
              <ul
                data-testid="protected-paths-categories"
                style={{ listStyle: 'none', margin: '10px 0 0 0', padding: 0 }}
              >
                {state.categories.map((row) => (
                  <li
                    key={row.label}
                    style={{ padding: '8px 0', borderBottom: `1px solid ${C.borderSoft}` }}
                  >
                    <div style={{
                      fontFamily: monoFont, fontSize: 12,
                      color: C.text, overflowWrap: 'anywhere',
                    }}>
                      {row.label}
                    </div>
                    <div style={{
                      fontFamily: sansFont, fontSize: 12, color: C.muted, marginTop: 2,
                    }}>
                      {row.description}
                    </div>
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
        </div>
      </Card>
      <ConfirmDialog
        open={confirmFlip === 'enforce'}
        onClose={() => setConfirmFlip(null)}
        title={`Block attack paths on ${site.domain}?`}
        confirmLabel="Turn on attack paths"
        cancelLabel="Not now"
        confirmTone="lime"
        onConfirm={() => flipMode('enforce')}
        body={(
          <div>
            <p style={{ fontFamily: sansFont, fontSize: 13, color: C.text, margin: '0 0 8px' }}>
              Requests for the attack paths on this list will get a plain “not found” from Sill’s Worker instead of reaching your server. In the last {window_days} days this would have stopped {count.toLocaleString('en-US')} request{count === 1 ? '' : 's'}.
            </p>
            <p style={{ fontFamily: sansFont, fontSize: 12, color: C.muted, margin: 0 }}>
              Normal pages are not affected. You can turn it off again with one click.
            </p>
          </div>
        )}
      />
      <ConfirmDialog
        open={confirmFlip === 'observe'}
        onClose={() => setConfirmFlip(null)}
        title={`Turn off attack paths on ${site.domain}?`}
        confirmLabel="Turn off attack paths"
        cancelLabel="Keep them on"
        confirmTone="amber"
        onConfirm={() => flipMode('observe')}
        body={(
          <div>
            <p style={{ fontFamily: sansFont, fontSize: 13, color: C.text, margin: '0 0 8px' }}>
              Requests for these attack paths will reach your server again. Sill keeps counting them, and you can turn blocking back on any time.
            </p>
          </div>
        )}
      />
    </>
  );
}

function describeModeError(code: string): string {
  if (code === 'forbidden') return 'You do not have permission to change this setting.';
  if (code === 'unauthorized') return 'Your session expired. Sign in again.';
  if (code === 'rate_limited') return 'Too many changes. Try again in a moment.';
  if (code === 'not_found') return 'This site could not be found.';
  if (code === 'bad_request') return 'The request was rejected.';
  if (code === 'install_cannot_enforce') return 'Your Worker is below the version that reads these settings. Update your Cloudflare Worker.';
  return 'Could not change the mode.';
}

const PATTERN_ERROR_MESSAGES: Record<string, string> = {
  empty: 'The pattern cannot be empty.',
  too_long: 'The pattern is too long.',
  non_printable: 'The pattern contains characters we cannot store.',
  must_start_with_slash: 'The pattern must start with /.',
  too_many_wildcards: 'The pattern can only use one * character.',
  wildcard_in_middle: 'The * character must be at the start or end of the pattern.',
  suffix_pattern_missing_leading_slash: 'A leading * must be followed by /.',
  well_known_forbidden: '/.well-known/ paths cannot be added — they are reserved.',
  // MAJOR 1 (2026-10-18 security review).
  root_pattern_forbidden: 'A root pattern (/, /*, */) would refuse every request. Narrow the path.',
  pattern_too_broad: 'A prefix must be at least 3 characters (e.g. /app* not /a*).',
};

export interface ProtectedPathsListEditorProps {
  readonly kind: 'custom' | 'exceptions';
  readonly site_id: string;
  readonly http: ReturnType<typeof useOptionalHttp>;
  readonly canMutate: boolean;
  readonly cap: number;
  readonly maxLength: number;
  readonly entries: readonly string[];
  readonly onSaved: () => Promise<void>;
}

export function ProtectedPathsListEditor({
  kind, site_id, http, canMutate, cap, maxLength, entries, onSaved,
}: ProtectedPathsListEditorProps): JSX.Element {
  const C = useC();
  const [draft, setDraft] = useState<readonly string[]>(entries);
  const [adding, setAdding] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<number | null>(null);
  useEffect(() => {
    setDraft(entries);
    setError(null);
  }, [entries]);
  const dirty = useMemo(() => {
    if (draft.length !== entries.length) return true;
    for (let i = 0; i < draft.length; i += 1) {
      if (draft[i] !== entries[i]) return true;
    }
    return false;
  }, [draft, entries]);
  const label = kind === 'custom' ? 'Custom paths' : 'Allow exceptions';
  const addLabel = kind === 'custom' ? 'Add path' : 'Add exception';
  const legend = kind === 'custom'
    ? `Refuse additional paths beyond the built-in list. Up to ${cap}. Patterns can be exact (/foo/bar), prefix (/foo/*), or suffix (*/bar). Custom paths are delivered to your Worker in a signed file that isn't secret — prefer attacker-shaped probe paths over naming your internal areas.`
    : `Exempt paths from refusal — Sill will NOT refuse these, even if the built-in list matches. Up to ${cap}.`;

  const tryAdd = useCallback(() => {
    const next = adding.trim();
    if (next.length === 0) return;
    if (next.length > maxLength) {
      setError(`The pattern is longer than ${maxLength} characters.`);
      return;
    }
    if (draft.length >= cap) {
      setError(`You've reached the limit of ${cap}. Remove one to add another.`);
      return;
    }
    setDraft([...draft, next]);
    setAdding('');
    setError(null);
  }, [adding, cap, draft, maxLength]);

  const remove = useCallback((idx: number) => {
    setDraft(draft.filter((_, i) => i !== idx));
    setError(null);
  }, [draft]);

  const save = useCallback(async () => {
    if (http === null) return;
    setSaving(true);
    setError(null);
    const r = kind === 'custom'
      ? await putProtectedPathsCustom(http, site_id, draft)
      : await putProtectedPathsExceptions(http, site_id, draft);
    if (!r.ok) {
      if (r.error === 'invalid_pattern') {
        const msg = PATTERN_ERROR_MESSAGES[r.reason] ?? 'Pattern not accepted.';
        setError(`Entry ${r.index + 1} (${r.raw.slice(0, 40)}): ${msg}`);
      } else if (r.error === 'cap_exceeded') {
        setError(`You've reached the limit of ${cap}.`);
      } else if (r.error === 'plan_feature_required') {
        // ADR-0263 S4 — site's resolved v2 plan does not include
        // `custom_attack_paths`. Show the server's plain-copy message.
        setError(r.plan_message);
      } else {
        setError(describeModeError(r.error));
      }
      setSaving(false);
      return;
    }
    setSavedAt(Date.now());
    setSaving(false);
    await onSaved();
  }, [cap, draft, http, kind, onSaved, site_id]);

  const sectionId = kind === 'custom' ? 'protected-paths-custom' : 'protected-paths-exceptions';

  return (
    <div style={{ marginTop: 20 }} data-testid={sectionId}>
      <div style={{
        fontFamily: monoFont, fontSize: 9, letterSpacing: 2,
        color: C.muted, textTransform: 'uppercase', marginBottom: 4,
      }}>
        {label} &middot; {draft.length} / {cap}
      </div>
      <div style={{ fontFamily: sansFont, fontSize: 12, color: C.muted, marginBottom: 8 }}>
        {legend}
      </div>
      {draft.length === 0 ? (
        <div
          style={{
            fontFamily: sansFont, fontSize: 12, color: C.muted,
            padding: '8px 0', borderBottom: `1px solid ${C.borderSoft}`,
          }}
        >
          No entries.
        </div>
      ) : (
        <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
          {draft.map((p, i) => (
            <li
              key={`${p}-${String(i)}`}
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr auto',
                gap: 12,
                alignItems: 'center',
                padding: '6px 0',
                borderBottom: `1px solid ${C.borderSoft}`,
              }}
            >
              <div style={{
                fontFamily: monoFont, fontSize: 12, color: C.text,
                overflowWrap: 'anywhere',
              }}>
                {p}
              </div>
              {canMutate ? (
                <button
                  type="button"
                  data-testid={`${sectionId}-remove-${String(i)}`}
                  onClick={() => remove(i)}
                  style={{
                    background: 'transparent',
                    color: C.text,
                    border: `1px solid ${C.border}`,
                    borderRadius: 2,
                    padding: '4px 10px',
                    fontFamily: sansFont, fontSize: 11,
                    cursor: 'pointer',
                  }}
                >
                  Remove
                </button>
              ) : null}
            </li>
          ))}
        </ul>
      )}
      {canMutate ? (
        <div style={{ marginTop: 10, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <input
            data-testid={`${sectionId}-input`}
            type="text"
            value={adding}
            onChange={(e) => setAdding(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') { e.preventDefault(); tryAdd(); }
            }}
            placeholder={kind === 'custom' ? '/wp-admin/* or /debug/foo.log' : '*/public/debug.log'}
            maxLength={maxLength + 1}
            style={{
              flex: '1 1 220px',
              fontFamily: monoFont, fontSize: 12, color: C.text,
              background: 'transparent',
              border: `1px solid ${C.border}`,
              borderRadius: 2,
              padding: '6px 10px',
            }}
          />
          <button
            type="button"
            data-testid={`${sectionId}-add`}
            onClick={tryAdd}
            style={{
              background: 'transparent',
              color: C.text,
              border: `1px solid ${C.border}`,
              borderRadius: 2,
              padding: '6px 12px',
              fontFamily: sansFont, fontSize: 12,
              cursor: 'pointer',
            }}
          >
            {addLabel}
          </button>
          <button
            type="button"
            data-testid={`${sectionId}-save`}
            disabled={!dirty || saving}
            onClick={save}
            style={{
              background: dirty ? C.lime : 'transparent',
              color: dirty ? C.ink : C.muted,
              border: `1px solid ${C.border}`,
              borderRadius: 2,
              padding: '6px 14px',
              fontFamily: sansFont, fontSize: 12, fontWeight: 600,
              cursor: dirty && !saving ? 'pointer' : 'default',
            }}
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
          {savedAt !== null && !dirty ? (
            <span
              data-testid={`${sectionId}-saved-indicator`}
              style={{ fontFamily: sansFont, fontSize: 12, color: C.muted, alignSelf: 'center' }}
            >
              Saved.
            </span>
          ) : null}
        </div>
      ) : null}
      {error !== null ? (
        <div
          data-testid={`${sectionId}-error`}
          style={{
            marginTop: 8,
            padding: '6px 10px',
            border: `1px solid ${C.coral}`,
            borderRadius: 2,
            fontFamily: sansFont, fontSize: 12, color: C.text,
          }}
        >
          {error}
        </div>
      ) : null}
    </div>
  );
}
